import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Coffee, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatMoney } from "@/lib/currency";
import { toast } from "sonner";

const CATS = ["coffee", "cold-brew", "tea", "food", "pastry"];
const BREWED_CATS = new Set(["coffee", "cold-brew"]);

const EMPTY_FORM = {
  name: "", category: "coffee", price: "", description: "", image_url: "",
  coffee_stock_id: "", coffee_grams_per_unit: "",
};

export default function Menu() {
  const [items, setItems] = useState([]);
  const [coffeeSkus, setCoffeeSkus] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    const [menuRes, skuRes] = await Promise.all([
      api.get("/cafe/menu"),
      api.get("/cafe/coffee-inventory").catch(() => ({ data: [] })),
    ]);
    setItems(menuRes.data);
    setCoffeeSkus(skuRes.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      category: form.category,
      price: Number(form.price),
      description: form.description || null,
      image_url: form.image_url || null,
      coffee_stock_id: form.coffee_stock_id || null,
      coffee_grams_per_unit: form.coffee_grams_per_unit
        ? Number(form.coffee_grams_per_unit)
        : null,
    };
    try {
      await api.post("/cafe/menu", payload);
      toast.success(`${form.name} added.`);
      setOpen(false);
      setForm(EMPTY_FORM);
      await load();
    } catch {
      toast.error("Could not save item.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/cafe/menu/${id}`);
    await load();
  };

  const isBrewed = BREWED_CATS.has(form.category);

  return (
    <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Café</div>
          <h1 className="font-display text-4xl">Menu</h1>
          <p className="text-muted-foreground text-sm mt-1">{items.length} items live on your POS.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full">+ New item</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((m) => (
          <div key={m.id} className="rounded-xl border border-border bg-card overflow-hidden group">
            <div className="aspect-video bg-muted overflow-hidden">
              {m.image_url ? (
                <img src={m.image_url} className="h-full w-full object-cover" alt="" />
              ) : (
                <div className="h-full w-full grid place-items-center text-muted-foreground">
                  <Coffee className="h-8 w-8" />
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-display text-xl leading-tight truncate">{m.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{m.category}</div>
                </div>
                <div className="font-display text-xl">{formatMoney(m.price)}</div>
              </div>
              {m.description && (
                <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{m.description}</div>
              )}
              {m.coffee_stock_id && m.coffee_grams_per_unit ? (
                <div
                  data-testid={`menu-item-linked-${m.id}`}
                  className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-[10px] uppercase tracking-[0.16em] px-2 py-0.5"
                >
                  <Coffee className="h-3 w-3" /> {m.coffee_grams_per_unit}g / drink
                </div>
              ) : null}
              <div className="mt-3 pt-3 border-t border-border/60 flex justify-end">
                <button onClick={() => remove(m.id)} className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Add menu item</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Image URL (optional)</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" className="mt-1.5" />
            </div>

            {isBrewed && (
              <div className="rounded-lg border border-border/70 bg-secondary/40 p-3 space-y-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  Auto-log coffee usage (optional)
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Coffee SKU</Label>
                    <Select
                      value={form.coffee_stock_id || "none"}
                      onValueChange={(v) => setForm({ ...form, coffee_stock_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger data-testid="menu-form-coffee-sku" className="mt-1.5">
                        <SelectValue placeholder="No link" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No link</SelectItem>
                        {coffeeSkus.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name} ({s.unit_type})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Grams per drink</Label>
                    <Input
                      data-testid="menu-form-grams"
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="18"
                      value={form.coffee_grams_per_unit}
                      onChange={(e) => setForm({ ...form, coffee_grams_per_unit: e.target.value })}
                      className="mt-1.5"
                      disabled={!form.coffee_stock_id}
                    />
                  </div>
                </div>
                {coffeeSkus.length === 0 && (
                  <div className="text-xs text-muted-foreground">
                    Add a coffee SKU in Coffee Inventory first to enable auto-deduction.
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button type="submit" className="rounded-full">Add item</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
